/**
 * ThreeUp component is used to display three pieces of content of equal importance
 *
 * @module views/components/ThreeUp
 * @memberof -Common
 */
import './ThreeUp.scss';

import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Image from '@ulta/core/components/Image/Image';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Spacer from '@ulta/core/components/Spacer/Spacer';
import Text from '@ulta/core/components/Text/Text';
import UltaAsset from '@ulta/core/components/UltaAsset/UltaAsset';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import Tag from '@ulta/modules/Tag/Tag';

import { constants } from '@ulta/utils/constants/constants';

/**
  * Represents a ThreeUp component
  *
  * @method
  * @param {ThreeUpProps} props - React properties passed from composition
  * @returns ThreeUp
  */
export const ThreeUp = function( props ){
  const titleProps = props?.title;
  const threeUpRef = useRef( null );
  const [currentThreeUpRef, setCurrentThreeUpRef] = useState( '' );
  const { rootMargin = defaultProps.rootMargin, root = defaultProps.root, threshold = defaultProps.threshold } = props;
  const isIntersecting = useIntersectionObserver( threeUpRef, {
    root: root,
    rootMargin: rootMargin,
    threshold: threshold
  }, handleIntersection( props ) );


  useEffect( () => {
    // set threeUpRef
    if( threeUpRef.current ){
      setCurrentThreeUpRef( threeUpRef.current );
    }
  }, [] );

  return (
    <div className={
      classNames( 'ThreeUp', {
        ...props.backgroundColor ? { ['ThreeUp__backgroundColor--' + props.backgroundColor]: true } : {},
        'ThreeUp__padding':props.backgroundColor
      } )
    }
    ref={ threeUpRef }
    >
      {
        props.backgroundImage && props.backgroundImage.imageUrl &&
        <div className='ThreeUp__backgroundImg'>
          <Image
            src={ props.backgroundImage.imageUrl }
            { ...spreadProps( { metaData: props.backgroundImage.metaData } ) }
          />
        </div>
      }
      <div className='ThreeUp__wrapper'>
        <div className='ThreeUp__contentSection'>
          {
            ( titleProps?.text || props?.subtitle ) &&
            <div className={
              classNames( 'ThreeUp__heading', {
                'ThreeUp__heading--Position': props.backgroundImage
              } )
            }
            >
              {
                titleProps &&
                <div className='ThreeUp__headingTitle'>
                  <Text
                    htmlTag={ titleProps.htmlTag }
                    textStyle={ titleProps.textStyle }
                    color={ titleProps.color }
                    textAlign={ titleProps.textAlign || 'center' }
                  >
                    { titleProps.text }
                  </Text>
                  { titleProps.spacerValue &&
                    <Spacer value={ titleProps.spacerValue } />
                  }
                </div>
              }
              {
                props.subtitle &&
                <div className='ThreeUp__headingSubtitle'>
                  <Text
                    htmlTag='p'
                    textStyle='subtitle-2'
                    textAlign='center'
                  >
                    { props.subtitle }
                  </Text>
                </div>
              }
            </div>
          }
          <div className={
            classNames( 'ThreeUp__article', {
              'ThreeUp__article--Position': ( !titleProps?.text && !props.subtitle ) && props.backgroundImage
            } )
          }
          >
            {
              props.article.map( ( content, index ) => (
                <div className='ThreeUp__content'
                  key={ index }
                >
                  <div className='ThreeUp__contentGrid'>
                    <div className='ThreeUp__contentImage'>
                      {
                        content.image &&
                        <UltaAsset
                          { ...spreadProps( { ...content.image } ) }
                          shouldUseIntersectionObserver={ false }
                        />
                      }
                    </div>
                    <div className='ThreeUp__contentData'>
                      {
                        content.tag &&
                        <div className='ThreeUp__contentTag'>
                          <Tag
                            style='default'
                            message={ content.tag }
                          />
                        </div>
                      }

                      {
                        content.eyebrow &&
                        <div className='ThreeUp__contentEyebrow'>
                          <Text
                            htmlTag='p'
                            textStyle='eyebrow'
                          >
                            { content.eyebrow }
                          </Text>
                        </div>
                      }

                      <div className='ThreeUp__contentTitle'>
                        <Text
                          htmlTag='h3'
                          textStyle='title-6'
                        >
                          { content.title }
                        </Text>
                      </div>
                      <div className='ThreeUp__contentTitlesupport'>
                        <Text
                          htmlTag='p'
                          textStyle='body-2'
                        >
                          { content.titleSupport }
                        </Text>
                      </div>
                      {
                        content.linkAction && content.linkAction.label &&
                        <div className='ThreeUp__contentLabel'>
                          <Link_Huge
                            target='_self'
                            withHover
                            ariaLabel={ content.linkAction.label + ' ' + content.title }
                            action={ content.linkAction }
                          >
                            <Text
                              htmlTag='span'
                              textStyle='body-2'
                            >
                              { content.linkAction.label }
                            </Text>
                          </Link_Huge>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              ) )
            }
          </div>
        </div>
      </div>
    </div>
  );
};

/**
   * Property type definitions
   * @typedef ThreeUpProps
   * @type {object}
   * @property {object} title - Sets the title properties
   * @property {string} subtitle - Sets the subtitle
   * @property {string} backgroundImage - Sets the backgroundImage
   * @property {string} backgroundColor - Sets the backgroundColor
   * @property {array} content - Sets the content
   */

export const propTypes =  {
  title: PropTypes.object,
  subtitle: PropTypes.string,
  backgroundColor: PropTypes.string,
  content: PropTypes.array
};

export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

ThreeUp.defaultProps = defaultProps;
ThreeUp.propTypes = propTypes;

export default ThreeUp;